import { useEffect, useState } from 'react';
import { EstageLogo } from '@/components/landing';
import { fetchPublishedPosts, type BlogPostSummary } from '@/services/blogService';

interface BlogListViewProps {
  onNavigatePost: (slug: string) => void;
  onNavigateHome: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export const BlogListView: React.FC<BlogListViewProps> = ({ onNavigatePost, onNavigateHome }) => {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPublishedPosts()
      .then(setPosts)
      .catch(() => setError('Beiträge konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="landing-root blog-root">
      {/* Navbar */}
      <nav className="lp-nav">
        <div className="lp-nav-container">
          <a href="/" className="lp-nav-logo" onClick={(e) => { e.preventDefault(); onNavigateHome(); }}>
            <EstageLogo />
          </a>
          <div className="lp-nav-links">
            <a href="/" onClick={(e) => { e.preventDefault(); onNavigateHome(); }}>← Zurück zur Startseite</a>
          </div>
          <div className="lp-nav-actions">
            <a href="/" className="lp-btn-nav-cta" onClick={(e) => { e.preventDefault(); onNavigateHome(); }}>
              Jetzt kostenlos testen
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/>
              </svg>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="blog-hero">
        <div className="blog-hero-inner">
          <span className="blog-hero-badge">Blog</span>
          <h1>Insights rund um<br />virtuelles Home Staging</h1>
          <p className="blog-hero-sub">
            Tipps, Trends und Erfolgsgeschichten für Immobilienprofis.
          </p>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="blog-list-section">
        <div className="blog-list-inner">
          {loading && (
            <div className="blog-loading">
              <div className="blog-spinner" />
            </div>
          )}

          {error && (
            <div className="blog-error">{error}</div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="blog-empty">
              <p>Noch keine Beiträge veröffentlicht. Schau bald wieder vorbei!</p>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <div className="blog-grid">
              {posts.map((post, i) => (
                <article
                  key={post.id}
                  className={`blog-card${i === 0 ? ' blog-card--featured' : ''}`}
                  onClick={() => onNavigatePost(post.slug)}
                >
                  {post.cover_image_url && (
                    <div className="blog-card-image">
                      <img src={post.cover_image_url} alt={post.title} loading="lazy" />
                    </div>
                  )}
                  <div className="blog-card-body">
                    {post.tags.length > 0 && (
                      <div className="blog-card-tags">
                        {post.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="blog-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <h2 className="blog-card-title">{post.title}</h2>
                    {post.excerpt && (
                      <p className="blog-card-excerpt">{post.excerpt}</p>
                    )}
                    <div className="blog-card-meta">
                      <span>{post.author}</span>
                      {post.published_at && <span>·</span>}
                      {post.published_at && <span>{formatDate(post.published_at)}</span>}
                      {post.reading_time_minutes && <span>·</span>}
                      {post.reading_time_minutes && <span>{post.reading_time_minutes} Min. Lesezeit</span>}
                    </div>
                    <button className="blog-card-cta">
                      Weiterlesen
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                      </svg>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="blog-footer">
        <div className="blog-footer-inner">
          <span>&copy; 2026 Estage.Ai</span>
          <a href="/" onClick={(e) => { e.preventDefault(); onNavigateHome(); }}>Zurück zur Startseite</a>
        </div>
      </div>
    </div>
  );
};
